import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { incomeAPI, rankAPI, userAPI } from '../../api';
import LoadingSpinner from '../LoadingSpinner';

const UserDashboard = () => {
  const { user } = useAuth();
  const [dashboardProfile, setDashboardProfile] = useState(null);
  const [incomeSummary, setIncomeSummary] = useState(null);
  const [rankInfo, setRankInfo] = useState(null);
  const [walletData, setWalletData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedIncomeType, setSelectedIncomeType] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, [user?._id, user?.id]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [profileRes, incomeRes, rankRes, walletRes] = await Promise.all([
        userAPI.getDashboardProfile(),
        incomeAPI.getIncomeSummary(),
        rankAPI.getMyRank(),
        userAPI.getWallet(),
      ]);

      setDashboardProfile(profileRes?.data?.data || null);
      setIncomeSummary(incomeRes.data);
      setRankInfo(rankRes.data);
      setWalletData(walletRes.data.wallet);
      setError('');
    } catch (err) {
      setError('Failed to load dashboard data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Income Types Data Structure based on DESi MART document
  const incomeTypes = [
    {
      id: 1,
      name: 'Direct Sponsor Bonus',
      percentage: '15%',
      description: 'Earn 15% bonus instantly when you sponsor a new member. This is your first and most immediate income stream — the more you sponsor, the more you earn.',
      minAmount: 'No minimum',
      color: 'from-blue-500 to-blue-600',
      icon: '👥',
      requirements: 'Direct sponsorship of new members',
      payoutFrequency: 'Instant'
    },
    {
      id: 2,
      name: 'Monthly Product Rewards',
      percentage: '₹1,000 - ₹15,000',
      description: 'Earn monthly product rewards based on your purchase volume. Higher purchases unlock bigger rewards for longer durations.',
      minAmount: '₹15,000/month',
      color: 'from-green-500 to-green-600',
      icon: '📦',
      tiers: [
        { volume: '₹15,000', reward: '₹1,000 for 3 months' },
        { volume: '₹25,000', reward: '₹3,000 for 3 months' },
        { volume: '₹50,000', reward: '₹5,000 for 3 months' },
        { volume: '₹1,00,000', reward: '₹6,000 for 6 months' }
      ],
      payoutFrequency: 'Monthly'
    },
    {
      id: 3,
      name: 'Daily Boosting Income',
      percentage: '10% | 5% | 3%',
      description: 'Accelerate your earnings with daily booster bonuses on top of your regular income. Three levels of boosters available.',
      minAmount: 'Based on activity',
      color: 'from-yellow-500 to-yellow-600',
      icon: '⚡',
      boosters: [
        { level: '1st Booster', percentage: '10%' },
        { level: '2nd Booster', percentage: '5%' },
        { level: '3rd Booster', percentage: '3%' }
      ],
      payoutFrequency: 'Daily'
    },
    {
      id: 4,
      name: 'Level Income (1-7)',
      percentage: '10% → 1%',
      description: 'Earn commission on repurchases made by your entire downline team. Higher percentages for closer team members.',
      minAmount: 'Requires active team',
      color: 'from-purple-500 to-purple-600',
      icon: '📊',
      levels: [
        { level: 1, percent: '10%' },
        { level: 2, percent: '5%' },
        { level: 3, percent: '3%' },
        { level: 4, percent: '2.5%' },
        { level: 5, percent: '1.5%' },
        { level: 6, percent: '1.5%' },
        { level: 7, percent: '1%' }
      ],
      payoutFrequency: 'On repurchase'
    },
    {
      id: 5,
      name: 'Level Income (8-15)',
      percentage: '1% → 0.50%',
      description: 'Extended deep-level commissions for your growing organization. Maintain residual income from deeper team levels.',
      minAmount: 'Deep team building',
      color: 'from-indigo-500 to-indigo-600',
      icon: '🔮',
      levels: [
        { level: 8, percent: '1%' },
        { level: 9, percent: '1%' },
        { level: 10, percent: '1%' },
        { level: 11, percent: '0.50%' },
        { level: 12, percent: '0.50%' },
        { level: 13, percent: '0.50%' },
        { level: 14, percent: '0.50%' },
        { level: 15, percent: '0.50%' }
      ],
      payoutFrequency: 'On repurchase'
    },
    {
      id: 6,
      name: 'Lifetime Rewards (Ranks 1-6)',
      percentage: '₹5K - ₹2.5L',
      description: 'One-time reward payouts as you achieve ranks from Star to Blue Diamond based on your team business volume.',
      minAmount: '1 Lac - 50 Lacs',
      color: 'from-red-500 to-red-600',
      icon: '🏆',
      ranks: [
        { rank: 'Star', business: '1 - 1 Lac', reward: '₹5,000' },
        { rank: 'Silver Star', business: '2.5 - 2.5 Lacs', reward: '₹12,500' },
        { rank: 'Gold', business: '5 - 5 Lacs', reward: '₹25,000' },
        { rank: 'Platinum', business: '10 - 10 Lacs', reward: '₹50,000' },
        { rank: 'Diamond', business: '25-25 Lacs', reward: '₹1,25,000' },
        { rank: 'Blue Diamond', business: '50 - 50 Lacs', reward: '₹2,50,000' }
      ],
      payoutFrequency: 'One-time'
    },
    {
      id: 7,
      name: 'Elite Lifetime Rewards (Ranks 7-12)',
      percentage: '₹5L - ₹50Cr',
      description: 'Extraordinary rewards for top achievers. Unlock life-changing payouts as you reach the highest ranks.',
      minAmount: '1 Cr - 500 Cr',
      color: 'from-pink-500 to-pink-600',
      icon: '👑',
      eliteRanks: [
        { rank: 'Royal Diamond', business: '1 - 1 Cr.', reward: '₹5,00,000' },
        { rank: 'Crown Diamond', business: '2 - 2 Cr.', reward: '₹10,00,000' },
        { rank: 'Crown Ambassador', business: '5 - 5 Cr.', reward: '₹25,00,000' },
        { rank: 'Double Crown Star', business: '15 - 15 Cr.', reward: '₹75,00,000' },
        { rank: 'Director Star', business: '100 - 100 Cr.', reward: '₹5 Cr.' },
        { rank: 'Director Club', business: '500 - 500 Cr.', reward: '₹25 Cr.' }
      ],
      payoutFrequency: 'One-time'
    },
    {
      id: 8,
      name: 'Real-Time Bonanza',
      percentage: '10×10 Formula',
      description: 'Build your account network exponentially over 5 months. Each person sponsors 10, creating massive growth potential.',
      minAmount: 'Requires 10 direct sponsors',
      color: 'from-cyan-500 to-cyan-600',
      icon: '🚀',
      growth: [
        { month: 'Month 1', accounts: '10' },
        { month: 'Month 2', accounts: '100' },
        { month: 'Month 3', accounts: '1,000' },
        { month: 'Month 4', accounts: '10,000' },
        { month: 'Month 5', accounts: '1,00,000' }
      ],
      payoutFrequency: 'Monthly'
    },
    {
      id: 9,
      name: 'Royalty Club',
      percentage: 'Toyota Fortuner',
      description: 'Premium incentive for top performers. Luxury car reward with structured monthly payments.',
      minAmount: '₹1 Lac/month × 51 months',
      color: 'from-amber-500 to-amber-600',
      icon: '🚗',
      reward: 'Toyota Fortuner (Full Payment)',
      terms: '₹1,00,000/month for 51 months',
      payoutFrequency: 'Monthly'
    },
    {
      id: 10,
      name: 'Re-Purchase Commission',
      percentage: 'Multi-level',
      description: 'Earn commissions on all product repurchases by your team members across multiple levels.',
      minAmount: 'Based on team volume',
      color: 'from-teal-500 to-teal-600',
      icon: '🔄',
      requirements: '10 Direct Sponsors Required',
      minPayout: '₹1,000',
      payoutFrequency: 'Bi-monthly (5th & 20th)'
    }
  ];

  const incomeWallets = walletData?.incomeWallets || {
    directSponsorBonus: 0,
    repurchaseLevelIncome: 0,
    lifeTimeRewards: 0,
    monthlyProductRewards: 0,
    dailyBoostingIncome: 0,
    realTimeBonanza: 0,
    directorRoyaltyClub: 0
  };

  const sevenIncomeWalletCards = [
    { key: 'directSponsorBonus', title: 'Direct Sponsor Bonus', icon: '👥', color: 'from-sky-600 to-blue-500', subtitle: 'Instant sponsor earnings' },
    { key: 'dailyBoostingIncome', title: 'Daily Boosting Income', icon: '⚡', color: 'from-amber-500 to-orange-500', subtitle: 'Daily booster credits' },
    { key: 'monthlyProductRewards', title: 'Monthly Product Rewards', icon: '📦', color: 'from-emerald-600 to-green-500', subtitle: 'Monthly reward pool' },
    { key: 'repurchaseLevelIncome', title: 'Re-Purchase Level Income', icon: '📊', color: 'from-violet-600 to-purple-500', subtitle: 'Level repurchase income' },
    { key: 'lifeTimeRewards', title: 'Life-Time Rewards', icon: '🏆', color: 'from-rose-600 to-pink-500', subtitle: 'Rank milestone rewards' },
    { key: 'realTimeBonanza', title: 'Real-Time Bonanza', icon: '🚀', color: 'from-cyan-600 to-teal-500', subtitle: 'Bonanza stage income' },
    { key: 'directorRoyaltyClub', title: 'Director Royalty Club', icon: '🚗', color: 'from-fuchsia-600 to-purple-600', subtitle: 'Director royalty pool' }
  ];

  const formatDate = (value) => {
    if (!value) return 'Not available';
    return new Date(value).toLocaleString('en-GB');
  };

  const directBusiness = dashboardProfile?.directBusiness || 0;
  const downlineBusiness = dashboardProfile?.downlineBusiness || 0;
  const totalBusiness = dashboardProfile?.totalBusiness || (directBusiness + downlineBusiness);

  if (loading) return <div className="flex items-center justify-center min-h-screen"><LoadingSpinner fullPage={false} /></div>;
  if (error) return <div className="flex items-center justify-center min-h-screen text-red-600">{error}</div>;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* <div className="mb-8 bg-white rounded-xl shadow-md p-6 border border-gray-200">
          <div className="flex flex-col sm:flex-row sm:flex-wrap gap-4 sm:gap-6">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Name</div>
              <div className="text-base font-bold text-[#0F2F3D]">{dashboardProfile?.name || user?.name || 'Not available'}</div>
            </div>

            <div className="hidden sm:block w-px h-8 bg-gray-200 self-center"></div>

            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">My Sponsor Code</div>
              <div className="text-base font-bold text-[#0F2F3D]">{dashboardProfile?.mySponsorCode || user?.mySponsorCode || 'Not available'}</div>
            </div>
          </div>
        </div> */}

        

        {/* Main Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6 mb-8">
          <div className="bg-gradient-to-br from-emerald-600 to-green-500 rounded-xl shadow-md p-6 text-white">
            <div className="flex items-center justify-between mb-2">
              <span className="text-white/90 text-sm font-semibold">Deposit Wallet</span>
              <span className="text-white/80 text-xl">💰</span>
            </div>
            <div className="text-lg font-bold">
              ₹{(walletData?.depositWallet || 0).toLocaleString()}
            </div>
            <div className="text-xs text-white/80 mt-1">Fund to Purchase Product</div>
          </div>

          <div className="bg-gradient-to-br from-lime-600 to-emerald-500 rounded-xl shadow-md p-6 text-white">
            <div className="flex items-center justify-between mb-2">
              <span className="text-white/90 text-sm font-semibold">Total Purchase</span>
              <span className="text-white/80 text-xl">🛒</span>
            </div>
            <div className="text-lg font-bold">₹{incomeSummary?.wallet?.totalPayout?.toLocaleString() || '0'}</div>
            <div className="text-xs text-white/80 mt-1">Products purchased</div>
          </div>

          <div className="bg-gradient-to-br from-yellow-500 to-amber-500 rounded-xl shadow-md p-6 text-white">
            <div className="flex items-center justify-between mb-2">
              <span className="text-white/90 text-sm font-semibold">Withdrawable Income</span>
              <span className="text-white/80 text-xl">💵</span>
            </div>
            <div className="text-lg font-bold">
              ₹{(walletData?.pendingAmount || 0).toLocaleString()}
            </div>
            <div className="text-xs text-white/80 mt-1">Available for payout request</div>
          </div>

          <div className="bg-gradient-to-br from-blue-600 to-cyan-600 rounded-xl shadow-md p-6 text-white">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="text-white/90 text-sm font-semibold">Main Wallet</span>
                <span className="text-white/70 text-xs">💼</span>
              </div>
              <span className="text-white/70 text-xs">👑</span>
            </div>
            <div className="text-lg font-bold mb-1">₹{(walletData?.mainWallet || walletData?.walletBalance || 0).toLocaleString()}</div>
            <div className="text-xs text-white/80">Total MLM income wallet</div>
          </div>

          <div className="bg-gradient-to-br from-purple-600 to-pink-600 rounded-xl shadow-md p-6 text-white">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="text-white/90 text-sm font-semibold">Repurchase Wallet</span>
                <span className="text-white/70 text-xs">🔁</span>
              </div>
            </div>
            <div className="text-lg font-bold mb-1">₹{(walletData?.repurchaseWallet || 0).toLocaleString()}</div>
            <div className="text-xs text-white/80">5% credited on each withdrawal</div>
          </div>

          <div className="bg-gradient-to-br from-blue-600 to-indigo-500 rounded-xl shadow-md p-6 text-white">
            <div className="flex items-center justify-between mb-2">
              <span className="text-white/90 text-sm font-semibold">Direct Business</span>
              <span className="text-white/80 text-xl">🧍</span>
            </div>
            <div className="text-lg font-bold">₹{directBusiness.toLocaleString()}</div>
            <div className="text-xs text-white/80 mt-1">Your own purchase volume</div>
          </div>

          <div className="bg-gradient-to-br from-purple-600 to-violet-500 rounded-xl shadow-md p-6 text-white">
            <div className="flex items-center justify-between mb-2">
              <span className="text-white/90 text-sm font-semibold">Downline Business</span>
              <span className="text-white/80 text-xl">👥</span>
            </div>
            <div className="text-lg font-bold">₹{downlineBusiness.toLocaleString()}</div>
            <div className="text-xs text-white/80 mt-1">Team purchase volume</div>
          </div>

          <div className="bg-gradient-to-br from-indigo-600 to-sky-500 rounded-xl shadow-md p-6 text-white">
            <div className="flex items-center justify-between mb-2">
              <span className="text-white/90 text-sm font-semibold">Total Business</span>
              <span className="text-white/80 text-xl">📈</span>
            </div>
            <div className="text-lg font-bold">₹{totalBusiness.toLocaleString()}</div>
            <div className="text-xs text-white/80 mt-1">Direct + Downline</div>
          </div>

          {/* Daily Boosting Income Stats */}
          {/* <div className="bg-gradient-to-br from-purple-600 to-indigo-600 rounded-xl shadow-md p-6 text-white">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="text-white/90 text-sm font-semibold">Daily Boosting</span>
                <span className="text-white/70 text-xs">⚡</span>
              </div>
              <span className="text-white/70 text-xs">🚀</span>
            </div>
            <div className="text-2xl font-bold mb-2">₹{(incomeWallets.dailyBoostingIncome || 0).toLocaleString()}</div>
            <div className="text-xs text-white/80 mb-3">
              {incomeSummary?.lastBoostingCalculation
                ? `Last: ${new Date(incomeSummary.lastBoostingCalculation).toLocaleDateString()}`
                : 'Waiting for calculation...'
              }
            </div>
            {incomeSummary?.boostingStatus && (
              <div className="mb-3">
                <span className={`px-2 py-1 rounded text-xs ${incomeSummary.boostingStatus === 'CALCULATED'
                  ? 'bg-green-500 text-white'
                  : 'bg-yellow-500 text-white'
                  }`}>
                  {incomeSummary.boostingStatus}
                </span>
              </div>
            )}
            <button
              onClick={triggerDailyBoosting}
              disabled={boostingLoading}
              className={`w-full py-2 px-3 rounded-lg text-xs font-semibold transition-colors ${boostingLoading
                ? 'bg-white/20 cursor-not-allowed'
                : 'bg-white text-purple-600 hover:bg-gray-100'
                }`}
            >
              {boostingLoading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-2 h-3 w-3 text-purple-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </span>
              ) : (
                <span className="flex items-center justify-center">
                  <span className="mr-1">🚀</span>
                  Trigger Now
                </span>
              )}
            </button>
          </div> */}
        </div>

        {/* 7 Income Wallets */}
        <div className="mb-8">
          <h3 className="text-2xl font-bold text-secondary mb-6 flex items-center gap-2">
            <span className="text-2xl">💼</span>
            7 Income Wallets
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {sevenIncomeWalletCards.map((incomeWallet) => (
              <div key={incomeWallet.key} className={`bg-gradient-to-br ${incomeWallet.color} rounded-xl shadow-md p-4 text-white`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-white/90 font-semibold">{incomeWallet.title}</span>
                  <span className="text-lg text-white/80">{incomeWallet.icon}</span>
                </div>
                <div className="text-xl font-bold">
                  ₹{(incomeWallets[incomeWallet.key] || 0).toLocaleString()}
                </div>
                <div className="text-xs text-white/80 mt-1">{incomeWallet.subtitle}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="mb-8 bg-white rounded-xl shadow-md p-5 border-l-4 border-[#E4572E]">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div>
              <h3 className="text-lg font-semibold text-[#0F2F3D]">Send Money to Desi Mart</h3>
              <p className="text-sm text-gray-600">Pay using QR and upload screenshot with UPI details.</p>
            </div>
            <Link
              to="/user/send-money"
              className="inline-flex items-center justify-center px-4 py-2 bg-[#E4572E] text-white rounded-lg hover:bg-[#cf4521] transition-colors"
            >
              Open Payment Page
            </Link>
          </div>
        </div>

        {/* Payout Information Bar */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 mb-8 border border-blue-200">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <span className="bg-blue-500 text-white px-3 py-1 rounded-full text-sm font-semibold">ℹ️</span>
              <span className="text-gray-700">Min Payout: ₹1,000</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Service Charges:</span>
              <span className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-sm font-semibold">10%</span>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-blue-200 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Account Created</div>
              <div className="text-base font-bold text-[#0F2F3D]">{formatDate(dashboardProfile?.createdAt || user?.createdAt || user?.joinedDate)}</div>
            </div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">First Order Date</div>
              <div className="text-base font-bold text-[#0F2F3D]">{formatDate(dashboardProfile?.firstPurchaseDate)}</div>
            </div>
          </div>
        </div>

        {/* All 10 Income Types Grid */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-secondary mb-6 flex items-center gap-2">
            <span className="bg-primary text-white px-3 py-1 rounded-lg text-sm">10</span>
            Income Streams Available
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {incomeTypes.map((income) => (
              <div
                key={income.id}
                className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 cursor-pointer"
                onClick={() => setSelectedIncomeType(selectedIncomeType === income.id ? null : income.id)}
              >
                {/* Header with gradient */}
                <div className={`bg-gradient-to-r ${income.color} p-4 text-white`}>
                  <div className="flex items-center justify-between">
                    <span className="text-3xl">{income.icon}</span>
                    <span className="text-xs font-semibold bg-white/20 px-2 py-1 rounded-full">
                      {income.payoutFrequency}
                    </span>
                  </div>
                  <h3 className="font-bold text-lg mt-2">{income.name}</h3>
                  <p className="text-white/90 text-2xl font-bold mt-1">{income.percentage}</p>
                </div>

                {/* Content */}
                <div className="p-4">
                  <p className="text-gray-600 text-sm mb-3">{income.description.substring(0, 80)}...</p>

                  {/* Quick Info */}
                  <div className="flex items-center justify-between text-xs border-t pt-3">
                    <span className="text-gray-500">Min: {income.minAmount}</span>
                    <span className="bg-gray-100 px-2 py-1 rounded-full text-gray-700">
                      Click for details
                    </span>
                  </div>

                  {/* Expanded Details */}
                  {selectedIncomeType === income.id && (
                    <div className="mt-4 pt-4 border-t border-gray-200 space-y-3 animate-fadeIn">
                      {/* Requirements */}
                      {income.requirements && (
                        <div className="text-sm">
                          <span className="font-semibold text-gray-700">Requires:</span>
                          <p className="text-gray-600">{income.requirements}</p>
                        </div>
                      )}

                      {/* Tiers */}
                      {income.tiers && (
                        <div>
                          <span className="font-semibold text-sm text-gray-700">Reward Tiers:</span>
                          <div className="grid grid-cols-2 gap-2 mt-2">
                            {income.tiers.map((tier, idx) => (
                              <div key={idx} className="bg-gray-50 p-2 rounded text-xs">
                                <span className="font-bold text-primary">{tier.volume}</span>
                                <p className="text-gray-600">{tier.reward}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Boosters */}
                      {income.boosters && (
                        <div>
                          <span className="font-semibold text-sm text-gray-700">Boosters:</span>
                          <div className="flex gap-2 mt-2">
                            {income.boosters.map((booster, idx) => (
                              <div key={idx} className="bg-yellow-50 px-3 py-1 rounded-full text-xs font-semibold text-yellow-700">
                                {booster.level}: {booster.percentage}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Levels */}
                      {income.levels && (
                        <div>
                          <span className="font-semibold text-sm text-gray-700">Commission Levels:</span>
                          <div className="flex flex-wrap gap-1 mt-2">
                            {income.levels.map((level, idx) => (
                              <span key={idx} className="bg-purple-50 text-purple-700 px-2 py-1 rounded text-xs font-semibold">
                                L{level.level}: {level.percent}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Ranks */}
                      {income.ranks && (
                        <div>
                          <span className="font-semibold text-sm text-gray-700">Rank Rewards:</span>
                          <div className="mt-2 space-y-1 max-h-32 overflow-y-auto">
                            {income.ranks.map((rank, idx) => (
                              <div key={idx} className="flex justify-between text-xs bg-gray-50 p-1 rounded">
                                <span className="font-medium">{rank.rank}</span>
                                <span className="text-primary font-bold">{rank.reward}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Elite Ranks */}
                      {income.eliteRanks && (
                        <div>
                          <span className="font-semibold text-sm text-gray-700">Elite Rewards:</span>
                          <div className="mt-2 space-y-1 max-h-32 overflow-y-auto">
                            {income.eliteRanks.map((rank, idx) => (
                              <div key={idx} className="flex justify-between text-xs bg-pink-50 p-1 rounded">
                                <span className="font-medium">{rank.rank}</span>
                                <span className="text-pink-600 font-bold">{rank.reward}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Growth */}
                      {income.growth && (
                        <div>
                          <span className="font-semibold text-sm text-gray-700">Growth Path:</span>
                          <div className="grid grid-cols-5 gap-1 mt-2">
                            {income.growth.map((month, idx) => (
                              <div key={idx} className="text-center">
                                <div className="text-xs font-bold text-cyan-600">{month.month}</div>
                                <div className="text-xs bg-cyan-50 rounded p-1">{month.accounts}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Additional Info */}
                      {income.reward && (
                        <div className="bg-amber-50 p-2 rounded-lg">
                          <span className="font-semibold text-sm text-amber-700">Reward:</span>
                          <p className="text-sm font-bold text-amber-800">{income.reward}</p>
                          <p className="text-xs text-amber-600">{income.terms}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Rank Progress */}
        {rankInfo && (
          <div className="mb-8">
            <h3 className="text-2xl font-bold text-secondary mb-6 flex items-center gap-2">
              <span className="text-2xl">📈</span>
              Your Rank Journey
            </h3>
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
                <div className="text-center p-4 bg-gradient-to-br from-primary/10 to-primary/5 rounded-lg">
                  <div className="text-sm text-gray-600 mb-2">Current Rank</div>
                  <h4 className="text-3xl font-bold text-primary mb-1">{rankInfo.currentRank.rankName}</h4>
                  <div className="text-sm text-gray-500">ID: {user?.distributorId}</div>
                </div>

                <div className="col-span-1 md:col-span-2">
                  <div className="flex justify-between text-sm text-gray-600 mb-2">
                    <span>Progress to Next Rank</span>
                    <span className="font-semibold text-primary">{rankInfo.progressPercentage?.toFixed(1)}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-4 mb-4">
                    <div
                      className="bg-gradient-to-r from-primary to-primary-light h-4 rounded-full transition-all duration-500 relative"
                      style={{ width: `${rankInfo.progressPercentage}%` }}
                    >
                      <span className="absolute right-0 -top-6 bg-primary text-white text-xs px-2 py-1 rounded">
                        {rankInfo.progressPercentage?.toFixed(0)}%
                      </span>
                    </div>
                  </div>

                  {rankInfo.nextRank && (
                    <div className="bg-blue-50 rounded-lg p-4">
                      <div className="flex justify-between items-center">
                        <div>
                          <span className="text-sm text-gray-600">Next Target:</span>
                          <h5 className="text-xl font-bold text-secondary">{rankInfo.nextRank.rankName}</h5>
                        </div>
                        <div className="text-right">
                          <span className="text-sm text-gray-600">Business Needed:</span>
                          <p className="text-lg font-bold text-primary">₹{rankInfo.businessToNextRank?.toLocaleString()}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Income Breakdown Table */}
        {incomeSummary?.incomeByType?.length > 0 && (
          <div className="mb-8">
            <h3 className="text-2xl font-bold text-secondary mb-6">💰 Income Breakdown by Type</h3>
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Income Type</th>
                    <th className="px-6 py-4 text-right text-sm font-semibold text-gray-700">Amount (₹)</th>
                    <th className="px-6 py-4 text-right text-sm font-semibold text-gray-700">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {incomeSummary.incomeByType.map((income, idx) => (
                    <tr key={idx} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <span className="text-2xl">
                            {income._id.includes('SPONSOR') && '👥'}
                            {income._id.includes('REWARD') && '🏆'}
                            {income._id.includes('LEVEL') && '📊'}
                            {income._id.includes('BOOSTING') && '⚡'}
                            {income._id.includes('BONANZA') && '🚀'}
                          </span>
                          <span className="font-medium text-secondary">{income._id?.replace(/_/g, ' ')}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right font-bold text-primary">₹{income.total?.toLocaleString()}</td>
                      <td className="px-6 py-4 text-right">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${income.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                          }`}>
                          {income.status || 'Pending'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Monthly Performance */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <div className="bg-gradient-to-br from-accent to-green-600 rounded-xl shadow-lg p-6 text-white">
            <div className="flex items-center gap-3 mb-3">
              <span className="text-3xl">📅</span>
              <div>
                <div className="text-white/90 text-sm font-semibold">This Month's Income</div>
                <div className="text-3xl font-bold">₹{incomeSummary?.monthlyIncome?.toLocaleString() || '0'}</div>
              </div>
            </div>
            <div className="border-t border-white/20 pt-3 mt-3">
              <div className="flex justify-between text-sm">
                <span>Target: ₹{incomeSummary?.monthlyTarget?.toLocaleString() || '0'}</span>
                <span>{((incomeSummary?.monthlyIncome / incomeSummary?.monthlyTarget) * 100).toFixed(0) || 0}% achieved</span>
              </div>
            </div>
          </div>

          {/* Daily Boosting Income Stats */}
          {/* <div className="bg-gradient-to-br from-purple-600 to-indigo-600 rounded-xl shadow-lg p-6 text-white">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <span className="text-3xl">⚡</span>
                <div>
                  <div className="text-white/90 text-sm font-semibold">Daily Boosting Income</div>
                  <div className="text-3xl font-bold">₹{incomeSummary?.dailyBoostingIncome?.toLocaleString() || '0'}</div>
                </div>
              </div>
              <span className="text-white/70 text-xs">🚀</span>
            </div>
            <div className="text-xs text-white/80 mb-3">
              {incomeSummary?.lastBoostingCalculation 
                ? `Last calculated: ${new Date(incomeSummary.lastBoostingCalculation).toLocaleString()}`
                : 'Waiting for calculation...'
              }
            </div>
            {incomeSummary?.boostingStatus && (
              <div className="mb-3">
                <span className={`px-2 py-1 rounded text-xs ${
                  incomeSummary.boostingStatus === 'CALCULATED' 
                    ? 'bg-green-500 text-white' 
                    : 'bg-yellow-500 text-white'
                }`}>
                  {incomeSummary.boostingStatus}
                </span>
              </div>
            )}
            <button
              onClick={triggerDailyBoosting}
              disabled={boostingLoading}
              className={`w-full py-2 px-4 rounded-lg text-sm font-semibold transition-colors ${
                boostingLoading
                  ? 'bg-gray-500 cursor-not-allowed'
                  : 'bg-white text-purple-600 hover:bg-gray-100'
              }`}
            >
              {boostingLoading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </span>
              ) : (
                '🚀 Trigger Daily Boosting'
              )}
            </button>
          </div> */}

          <div className="bg-gradient-to-br from-orange-500 to-red-600 rounded-xl shadow-lg p-6 text-white">
            <div className="flex items-center gap-3 mb-3">
              <span className="text-3xl">🎯</span>
              <div>
                <div className="text-white/90 text-sm font-semibold">Team Performance</div>
                <div className="text-2xl font-bold">{incomeSummary?.teamSize || 0}</div>
                <div className="text-xs text-white/80">Active members</div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default UserDashboard;